import type { ListItemButtonProps, ListItemProps } from '@mui/material';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
} from '@mui/material';
import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';

import * as ROUTES from '@constants/routes';

import { useGetStripeCustomer } from '@hooks/api/stripe';

import type { ManageAccountModel } from '@models/account';

import { capitalize, formatDate } from '@utils/format';

const messages = defineMessages({
  invoices: {
    id: 'paymentInformation.invoices',
    defaultMessage: 'Invoices',
  },
  nextPayment: {
    id: 'paymentInformation.nextPayment',
    defaultMessage: 'Next Payment',
  },
  paymentMethod: {
    id: 'paymentInformation.paymentMethod',
    defaultMessage: 'Payment Method',
  },
  billingAddress: {
    id: 'paymentInformation.billingAddress',
    defaultMessage: 'Billing Address',
  },
  renewalDate: {
    id: 'paymentInformation.renewalDate',
    defaultMessage: 'Renewal Date',
  },
  payment: {
    id: 'paymentInformation.payment',
    defaultMessage: 'Payment & Billing',
  },
});

interface PaymentInformationProps {
  account?: ManageAccountModel;
  loading: boolean;
}

const PaymentInformation: React.FC<PaymentInformationProps> = ({
  account,
  loading: baseLoading,
}) => {
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (!account?.stripe_customer_id && !baseLoading) {
      navigate(ROUTES.MANAGE_ACCOUNT);
    }
  }, [account?.stripe_customer_id, baseLoading]);

  const { data: stripeCustomer, loading: stripeCustomerLoading } =
    useGetStripeCustomer({
      account_id: account?.account_id ?? '',
    });
  const stripeInfo = stripeCustomer?.getStripeCustomer;

  const loading = baseLoading || stripeCustomerLoading;

  const secondaryAction = (
    <Icon color="text.primary" name="chevron-down" rotation={-90} type="fas" />
  );
  const listItemProps: ListItemProps = {
    divider: true,
  };
  const listItemButtonProps: ListItemButtonProps = {
    divider: true,
  };

  return (
    <ContentLoader loading={loading}>
      <Paper>
        <List>
          <ListItem {...listItemProps}>
            <ListItemText
              primary={formatMessage(messages.nextPayment)}
              secondary={`${
                stripeInfo?.nextPayment?.amount
                  ? `$ ${stripeInfo.nextPayment.amount}`
                  : ''
              } - ${formatDate({
                date: stripeInfo?.nextPayment?.date,
              })}`}
            />
          </ListItem>

          <ListItemButton
            {...listItemButtonProps}
            onClick={() => navigate(ROUTES.MANAGE_PAYMENT_DETAILS)}
          >
            <ListItemText
              primary={formatMessage(messages.paymentMethod)}
              secondary={`${capitalize(
                stripeInfo?.paymentMethod?.brand ?? ''
              )} **** ${stripeInfo?.paymentMethod?.last4}`}
            />
            {secondaryAction}
          </ListItemButton>

          <ListItemButton
            {...listItemButtonProps}
            onClick={() => navigate(ROUTES.MANAGE_BILLING_ADDRESS)}
          >
            <ListItemText
              primary={formatMessage(messages.billingAddress)}
              secondary={`${account?.billing_address}, ${account?.region_id} ${account?.zip_postal}`}
            />
            {secondaryAction}
          </ListItemButton>

          <ListItemButton onClick={() => navigate(ROUTES.MANAGE_INVOICES)}>
            <ListItemText primary={formatMessage(messages.invoices)} />
            {secondaryAction}
          </ListItemButton>
        </List>
      </Paper>
    </ContentLoader>
  );
};

export default PaymentInformation;
